// Leave management (HR side) for the HR module of Khyati Portal / RSR International.
// Two sub-tabs: Requests (approve/reject/apply-on-behalf) and Balances (year pivot).
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  HRC, fmtDate, Card, SectionTitle, SubTabs, Th, Td, Table,
  Pill, Btn, Stat, Field, input, Modal, Empty, Loading,
} from './hrkit';

const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => Number(v || 0);

// Day diff inclusive of both endpoints (e.g. Mon→Mon = 1, Mon→Tue = 2).
const dayDiff = (from, to) => {
  if (!from || !to) return 1;
  const a = new Date(from), b = new Date(to);
  if (isNaN(a) || isNaN(b)) return 1;
  const d = Math.round((b - a) / 86400000) + 1;
  return d > 0 ? d : 1;
};

const truncate = (s, n = 48) => {
  const t = String(s || '');
  return t.length > n ? t.slice(0, n - 1) + '…' : t;
};

// ───────────────────────────── Apply on behalf ─────────────────────────────
function ApplyModal({ onClose, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    employee_id: '', leave_type_id: '', from_date: today(), to_date: today(),
    days: 1, reason: '',
  });
  const [daysTouched, setDaysTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/hr/employees'),
      api.get('/hr/leave/types'),
    ]).then(([e, t]) => {
      if (!alive) return;
      setEmployees(e.data?.data || []);
      setTypes(t.data?.data || []);
    }).catch((x) => {
      if (alive) setErr(x.response?.data?.message || 'Could not load form data');
    });
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-suggest days from the date range unless the user has overridden it.
  const setDate = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (!daysTouched) next.days = dayDiff(next.from_date, next.to_date);
      return next;
    });
  };

  const valid = form.employee_id && form.leave_type_id && form.from_date && form.to_date && num(form.days) > 0;

  const submit = () => {
    if (!valid) { setErr('Please fill employee, type, dates and days.'); return; }
    setSaving(true);
    setErr('');
    api.post('/hr/leave/requests', {
      employee_id: form.employee_id,
      leave_type_id: form.leave_type_id,
      from_date: form.from_date,
      to_date: form.to_date,
      days: num(form.days),
      reason: form.reason,
    })
      .then(() => onSaved())
      .catch((e) => setErr(e.response?.data?.message || 'Could not submit request'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Apply Leave (on behalf)" width={560} onClose={onClose}>
      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Employee">
            <select value={form.employee_id} onChange={(e) => set('employee_id', e.target.value)} style={input}>
              <option value="">Select employee…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name} ({e.employee_code})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Leave Type">
            <select value={form.leave_type_id} onChange={(e) => set('leave_type_id', e.target.value)} style={input}>
              <option value="">Select type…</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.name}{t.is_paid === false ? ' (unpaid)' : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="From">
          <input type="date" value={form.from_date} onChange={(e) => setDate('from_date', e.target.value)} style={input} />
        </Field>
        <Field label="To">
          <input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setDate('to_date', e.target.value)} style={input} />
        </Field>

        <Field label="Days">
          <input type="number" min="0.5" step="0.5" value={form.days}
            onChange={(e) => { setDaysTouched(true); set('days', e.target.value); }}
            style={input} />
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 9 }}>
          <span style={{ fontSize: 11, color: HRC.muted }}>Suggested: {dayDiff(form.from_date, form.to_date)} day(s)</span>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Reason">
            <textarea value={form.reason} onChange={(e) => set('reason', e.target.value)} rows={3}
              placeholder="Reason for leave" style={{ ...input, resize: 'vertical' }} />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving} onClick={submit}>
          {saving ? 'Submitting…' : 'Submit Request'}
        </Btn>
      </div>
    </Modal>
  );
}

// ───────────────────────────── Requests tab ─────────────────────────────
const STATUS_TABS = [
  // The HR queue (decide) only ever holds requests that have cleared reliever consent. Those still awaiting a
  // reliever are SHOWN to HR (Rajan, 20 Oct 2026) but cannot be decided until a reliever accepts.
  { key: 'pending', label: 'Pending HR', status: 'pending_hr' },
  { key: 'awaiting', label: 'Awaiting reliever', status: 'pending_reliever' },
  { key: 'approved', label: 'Approved', status: 'approved' },
  { key: 'rejected', label: 'Rejected', status: 'rejected_by_hr' },
  { key: 'all', label: 'All', status: null },
];

function RequestsView({ onChange }) {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);     // id being decided
  const [reject, setReject] = useState(null); // row pending rejection
  const [note, setNote] = useState('');
  const [showApply, setShowApply] = useState(false);

  const status = STATUS_TABS.find((t) => t.key === tab)?.status ?? null;

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/leave/requests', { params: status ? { status } : {} })
      .then((r) => { if (alive) setRows(r.data?.data || []); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load leave requests');
        setRows([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [status]);

  useEffect(load, [load]);

  const decide = (id, decision, decisionNote) => {
    setBusy(id);
    api.patch(`/hr/leave/requests/${id}/decision`, { decision, decision_note: decisionNote || '' })
      .then(() => {
        setReject(null);
        setNote('');
        load();
        onChange?.();
      })
      .catch((e) => setErr(e.response?.data?.message || 'Could not save decision'))
      .finally(() => setBusy(null));
  };

  const pendingCount = rows.filter((r) => r.status === 'pending_hr' || r.status === 'pending').length;
  const tabs = STATUS_TABS.map((t) => ({ key: t.key, label: t.label }));

  return (
    <Card>
      <SectionTitle right={
        <Btn variant="primary" size="sm" onClick={() => setShowApply(true)}>+ Apply Leave (on behalf)</Btn>
      }>
        Leave Requests
      </SectionTitle>

      <SubTabs tabs={tabs} active={tab} onChange={setTab} />

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {!loading && rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Stat label="Showing" value={rows.length} sub={tab === 'all' ? 'all statuses' : tab} accent={HRC.navy} />
          {tab === 'all'
            ? <Stat label="Pending" value={pendingCount} accent={HRC.amber} />
            : <Stat label="Total Days" value={rows.reduce((s, r) => s + num(r.days), 0)} accent={HRC.green} />}
        </div>
      )}

      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty>No {tab === 'all' ? '' : tab + ' '}leave requests</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Employee</Th>
              <Th>Type</Th>
              <Th>From–To</Th>
              <Th style={{ textAlign: 'right' }}>Days</Th>
              <Th>Reliever</Th>
              <Th>Reason</Th>
              <Th>Status</Th>
              <Th>Applied</Th>
              <Th style={{ textAlign: 'right' }}>Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{r.full_name || '—'}</div>
                  <div style={{ fontSize: 11, color: HRC.muted }}>{r.employee_code || ''}</div>
                </Td>
                <Td>
                  <span style={{
                    display: 'inline-block', padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                    color: HRC.navy, background: HRC.violet, marginRight: 6,
                  }}>{r.leave_code || '—'}</span>
                  <span style={{ fontSize: 12, color: HRC.muted }}>{r.leave_name || ''}</span>
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  {fmtDate(r.from_date)} – {fmtDate(r.to_date)}
                  {r.half_day ? <div style={{ fontSize: 10, color: HRC.amber, fontWeight: 700 }}>Half day</div> : null}
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 600 }}>{num(r.days)}</Td>
                <Td>
                  {r.reliever_name
                    ? <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{r.reliever_name}</div>
                        {r.reliever_status === 'accepted'
                          ? <div style={{ fontSize: 11, color: HRC.green, fontWeight: 700 }}>✓ consented</div>
                          : r.reliever_status === 'declined'
                            ? <div style={{ fontSize: 11, color: HRC.red, fontWeight: 700 }}>declined</div>
                            : <div style={{ fontSize: 11, color: HRC.amber, fontWeight: 700 }}>not answered yet</div>}
                        {r.reliever_status === 'pending' && r.reliever_can_sign_in === false
                          ? <div style={{ fontSize: 10, color: HRC.red }}>cannot sign in to answer</div> : null}
                      </div>
                    : <span style={{ color: HRC.muted }}>—</span>}
                </Td>
                <Td style={{ maxWidth: 200, color: HRC.muted }} >
                  <span title={r.reason || ''}>{truncate(r.reason) || '—'}</span>
                </Td>
                <Td><Pill value={r.status} /></Td>
                <Td style={{ whiteSpace: 'nowrap', color: HRC.muted, fontSize: 12 }}>{fmtDate(r.created_at)}</Td>
                <Td style={{ textAlign: 'right' }}>
                  {(r.status === 'pending_hr' || r.status === 'pending') ? (
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Btn size="sm" variant="primary" disabled={busy === r.id}
                        style={{ background: HRC.green }}
                        onClick={() => decide(r.id, 'approved')}>Approve</Btn>
                      <Btn size="sm" variant="danger" disabled={busy === r.id}
                        onClick={() => { setReject(r); setNote(''); }}>Reject</Btn>
                    </div>
                  ) : (r.status === 'pending_reliever' || r.status === 'declined_by_reliever') ? (
                    <div style={{ fontSize: 11, color: HRC.muted, textAlign: 'right' }}>
                      <div>Waiting for a reliever{r.awaiting_reliever_since ? ` since ${fmtDate(r.awaiting_reliever_since)}` : ''}</div>
                      <div>HR decides once a reliever accepts</div>
                    </div>
                  ) : r.status === 'cancelled' ? (
                    <div style={{ fontSize: 11, color: HRC.muted, textAlign: 'right' }}>
                      <div>withdrawn{r.cancelled_by_name ? ` by ${r.cancelled_by_name}` : ''}</div>
                      {r.cancelled_at ? <div>{fmtDate(r.cancelled_at)}</div> : null}
                      {r.cancel_reason ? <div style={{ fontStyle: 'italic' }}>“{r.cancel_reason}”</div> : null}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: HRC.muted, textAlign: 'right' }}>
                      {r.approver_name ? <div>by {r.approver_name}</div> : null}
                      {r.approved_at ? <div>{fmtDate(r.approved_at)}</div> : null}
                      {r.decision_note ? <div style={{ fontStyle: 'italic' }}>“{r.decision_note}”</div> : null}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {reject && (
        <Modal title="Reject leave request" width={420} onClose={() => { setReject(null); setNote(''); }}>
          <div style={{ fontSize: 13, color: HRC.text, marginBottom: 4 }}>
            {reject.full_name} · {reject.leave_code}
          </div>
          <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 14 }}>
            {fmtDate(reject.from_date)} – {fmtDate(reject.to_date)} ({num(reject.days)} day{num(reject.days) === 1 ? '' : 's'})
          </div>
          <Field label="Decision note (optional)">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Why is this being rejected?"
              style={{ ...input, resize: 'vertical' }} />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => { setReject(null); setNote(''); }}>Cancel</Btn>
            <Btn variant="danger" disabled={busy === reject.id}
              onClick={() => decide(reject.id, 'rejected', note)}>Reject request</Btn>
          </div>
        </Modal>
      )}

      {showApply && (
        <ApplyModal
          onClose={() => setShowApply(false)}
          onSaved={() => { setShowApply(false); load(); onChange?.(); }}
        />
      )}
    </Card>
  );
}

// ───────────────────────────── Balances tab ─────────────────────────────
function BalancesView() {
  const [year, setYear] = useState(2026);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/leave/balances', { params: { year } })
      .then((r) => { if (alive) setRows(r.data?.data || []); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load balances');
        setRows([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year]);

  useEffect(load, [load]);

  // Pivot: one row per employee, columns per leave type code.
  const codes = [];
  const codeName = {};
  rows.forEach((r) => {
    if (!codes.includes(r.code)) { codes.push(r.code); codeName[r.code] = r.name; }
  });

  const byEmp = {};
  rows.forEach((r) => {
    const key = r.employee_code || r.full_name;
    if (!byEmp[key]) byEmp[key] = { full_name: r.full_name, employee_code: r.employee_code, cells: {} };
    byEmp[key].cells[r.code] = r;
  });
  const employees = Object.values(byEmp);

  const yearOpts = [2024, 2025, 2026, 2027];

  return (
    <Card>
      <SectionTitle right={
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          style={{ ...input, width: 'auto', padding: '7px 10px' }}>
          {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      }>
        Leave Balances — {year}
      </SectionTitle>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 600, marginBottom: 8 }}>
        {employees.length} employee{employees.length === 1 ? '' : 's'} · used / entitled (available)
      </div>

      {loading ? <Loading /> : employees.length === 0 ? (
        <Empty>No leave balances for {year}</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Employee</Th>
              {codes.map((c) => (
                <Th key={c} style={{ textAlign: 'center' }} title={codeName[c]}>{c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.employee_code || emp.full_name}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{emp.full_name || '—'}</div>
                  <div style={{ fontSize: 11, color: HRC.muted }}>{emp.employee_code || ''}</div>
                </Td>
                {codes.map((c) => {
                  const cell = emp.cells[c];
                  if (!cell) return <Td key={c} style={{ textAlign: 'center', color: HRC.muted }}>—</Td>;
                  const avail = cell.available != null
                    ? num(cell.available)
                    : num(cell.entitled) - num(cell.used);
                  return (
                    <Td key={c} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>
                        {num(cell.used)} <span style={{ color: HRC.muted, fontWeight: 400 }}>/ {num(cell.entitled)}</span>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: avail > 0 ? HRC.green : HRC.red }}>
                        {avail} left
                      </div>
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ───────────────────────────── Entry ─────────────────────────────
const TOP_TABS = [
  { key: 'requests', label: 'Requests' },
  { key: 'balances', label: 'Balances' },
];

export default function LeaveAdmin(props) {
  const { onChange } = props || {};
  const [tab, setTab] = useState('requests');

  return (
    <div>
      <SubTabs tabs={TOP_TABS} active={tab} onChange={setTab} />
      {tab === 'requests' ? <RequestsView onChange={onChange} /> : <BalancesView />}
    </div>
  );
}
